import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Dropdown,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  TextField,
  Toggle,
} from 'office-ui-fabric-react';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleContent from '../../components/SubModuleContent';
import SubModuleHeader from '../../components/SubModuleHeader';
import CalendarPicker from '../../components/CalendarPicker';
import PickerField from '../../components/PickerField';
import styles from './DriModuleForm.styles';
import { STATEMENT } from '../../constants';

const DriModuleForm = ({
  buttonText,
  formDescription,
  formId,
  isPublicPreview,
  inputs,
  onSubmit,
  pickerSuggestionsProps,
  showField,
  subModuleHeader,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [severeFailureMessage, setSevereFailureErrorMessage] = useState(false);

  const initialValuesState = {};
  inputs.forEach(input => {
    if (input !== null) {
      initialValuesState[input.id] = null;
      if (input.type === 'date' && input.selectedDate) {
        initialValuesState[input.id] = input.selectedDate;
      }
      if (input.type === 'dropdown' && input.config && input.config.defaultSelectedKeys) {
        const initialInputState = {};
        input.config.defaultSelectedKeys.forEach(key => {
          initialInputState[key] = true;
        });
        initialValuesState[input.id] = initialInputState;
      }
    }
  });
  const [values, setValues] = useState(initialValuesState);
  const setInputValue = inputId => {
    return inputValue => {
      const newValues = {};
      newValues[inputId] = inputValue;
      setValues({
        ...values,
        ...newValues,
      });
    };
  };
  const headerId = `${formId}-header`;
  const submitId = `${formId}-submit`;

  const submitForm = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');
    setSevereFailureErrorMessage('');

    let submitValues = {
      ...values,
      visibleToPartner: isPublicPreview || false,
      ...(isPublicPreview ? { status: { [STATEMENT.STATUS.PREVIEW]: true } } : {}),
    };
    if (Object.keys(values).length === 1) {
      submitValues = values[Object.keys(values)[0]];
    }

    await onSubmit(submitValues, {
      setSuccessMessage,
      setErrorMessage,
      setSevereFailureErrorMessage,
    });

    setIsSubmitting(false);
  };

  const inputForConfig = (key, { id, label, type, disabledIf, config = {} }) => {
    const inputConfig = { ...config };
    if (disabledIf && values[disabledIf]) inputConfig.disabled = true;
    if (disabledIf === 'disabledInProd') return <></>;
    switch (type) {
      case 'date':
        return (
          <div key={key}>
            <CalendarPicker
              {...inputConfig}
              label={label}
              showMonthPickerAsOverlay={false}
              isDayPickerVisible={false}
              isMonthPickerVisible
              showGoToToday={false}
              buttonString="Select"
              selectedDate={values[id]}
              onSelectDate={setInputValue(id)}
            />
          </div>
        );
      case 'text':
        return (
          <div key={key}>
            <TextField
              {...inputConfig}
              className={styles.textField}
              inputClassName={styles.textInput}
              label={label}
              resizable={false}
              inputId={`${formId}-input-${id}`}
              onChange={(_, value) => {
                setInputValue(id)(value);
              }}
            />
          </div>
        );
      case 'picker':
        return (
          <div key={key}>
            <PickerField
              {...inputConfig}
              label={label}
              inputId={`${formId}-input-${id}`}
              onChange={setInputValue(id)}
              pickerSuggestionsProps={pickerSuggestionsProps}
            />
          </div>
        );
      case 'dropdown':
        return (
          <div key={key}>
            <Dropdown
              {...inputConfig}
              label={<pre>{label}</pre>}
              inputId={`${formId}-input-${id}`}
              onChange={(_, value) => {
                const newValue = { ...(values[id] || {}) };
                newValue[value.key] = value.selected;
                setInputValue(id)(newValue);
              }}
            />
          </div>
        );
      case 'checkbox':
        return (
          <div key={key}>
            <Checkbox
              {...inputConfig}
              label={label}
              inputId={`${formId}-input-${id}`}
              onChange={(_, value) => {
                setInputValue(id)(value);
              }}
            />
          </div>
        );
      case 'toggle':
        return (
          <div key={key}>
            <Toggle
              {...inputConfig}
              label={label}
              onText="On"
              offText="Off"
              inputId={`${formId}-input-${id}`}
              onChange={(_, value) => {
                setInputValue(id)(value);
              }}
            />
          </div>
        );
      default:
        throw new Error('Input type must be specified');
    }
  };

  return (
    <Module className={styles.driModuleForm}>
      <ModuleContent>
        <SubModule closed>
          <SubModuleHeader id={headerId} data-testid={headerId}>
            {subModuleHeader}
          </SubModuleHeader>
          <SubModuleContent>
            {formDescription && <p className={styles.fieldLabel}>{formDescription}</p>}
            {showField &&
              inputs.map(input => (input !== null ? inputForConfig(input.id, input) : <></>))}
            <PrimaryButton
              disabled={isSubmitting}
              className={styles.submitButton}
              data-testid={submitId}
              onClick={submitForm}
            >
              {isSubmitting ? <Spinner size={SpinnerSize.medium} /> : buttonText}
            </PrimaryButton>
            {successMessage && (
              <MessageBar
                className={styles.messageBar}
                messageBarType={MessageBarType.success}
                isMultiline={false}
                onDismiss={() => setSuccessMessage('')}
              >
                <pre>{successMessage}</pre>
              </MessageBar>
            )}
            {errorMessage && (
              <MessageBar
                className={styles.messageBar}
                messageBarType={MessageBarType.severeWarning}
                isMultiline
                dismissButtonAriaLabel="Close"
                onDismiss={() => setErrorMessage('')}
              >
                <pre>{errorMessage}</pre>
              </MessageBar>
            )}
            {severeFailureMessage && (
              <MessageBar
                className={styles.messageBar}
                messageBarType={MessageBarType.severeWarning}
                isMultiline={false}
                dismissButtonAriaLabel="Close"
                onDismiss={() => setSevereFailureErrorMessage('')}
              >
                <pre>{severeFailureMessage}</pre>
              </MessageBar>
            )}
          </SubModuleContent>
        </SubModule>
      </ModuleContent>
    </Module>
  );
};

DriModuleForm.propTypes = {
  buttonText: PropTypes.string,
  formDescription: PropTypes.string,
  formId: PropTypes.string.isRequired,
  inputs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
      config: PropTypes.object,
    }),
  ),
  onSubmit: PropTypes.func.isRequired,
  pickerSuggestionsProps: PropTypes.object,
  showField: PropTypes.bool,
  isPublicPreview: PropTypes.bool,
  subModuleHeader: PropTypes.string,
};

DriModuleForm.defaultProps = {
  buttonText: 'Submit',
  formDescription: '',
  inputs: [],
  pickerSuggestionsProps: {},
  showField: true,
  isPublicPreview: false,
  subModuleHeader: 'subModuleHeader',
};

export default DriModuleForm;
